// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@sc/App/Components/Notify";
import submitRfqForm from "@sc/App/Handlers/NonStockRfqForm/SubmitRfqForm";
import { CustomAppState } from "@sc/App/Store/Reducers";
import { xIsAuthenticated } from "@sc/App/Store/Selectors/GeneralSelectors";
import TextArea from "@sc/MobiusOverrides/TextArea/TextArea";
import { XNGAGE_CUSTOM_WIDGET } from "@sc/XDL/constants";
import { xParserOptions, xValidateOnSubmit } from "@sc/XDL/Utilities";
import { safeJsonParse } from "@sc/XDL/Utilities";
import { maxLengthAttr } from "@sc/XDL/Utilities/validation";
import parse from "html-react-parser";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    heading = "heading",
    subheading = "subheading",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.heading]: string;
        [fields.subheading]: string;
    };
}

const mapDispatchToProps = {
    submitRfqForm,
};

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);

    const { shipToId, billToId } = session;
    const { salespersonInfo } = session.properties;
    const shipToState = getShipToState(state, shipToId);

    return {
        currentBillTo: getCurrentBillToState(state),
        salespersonInfo: safeJsonParse(salespersonInfo),
        shipToState,
        session,
        isAuthenticated: xIsAuthenticated(state),
        isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface NonStockFormStyles {
    form?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    textFieldsWrapper?: InjectableCss;
    sharedTextFieldStyles?: InjectableCss;
    submitWrapper?: InjectableCss;
    errorMsg?: InjectableCss;
    separator?: InjectableCss;
    infoWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    heading?: TypographyPresentationProps;
    subHeading?: TypographyPresentationProps;
    infoTextSmallHeading?: SmallHeadingAndTextStyles;
    linesWrapper?: InjectableCss;
    linesHeader?: InjectableCss;
    mfrNum?: InjectableCss;
    mfrName?: InjectableCss;
    itemDescription?: InjectableCss;
    lineWrapper?: InjectableCss;
    mfrNameField?: InjectableCss;
    mfrNumField?: InjectableCss;
    descriptionField?: InjectableCss;
    notesWrapper?: InjectableCss;
    addItemLink?: LinkPresentationProps;
    remove?: LinkPresentationProps;
}

export const nonStockFormStyles: NonStockFormStyles = {};

const styles = nonStockFormStyles;
const StyledForm = getStyledWrapper("form");

interface LineModel {
    manufacturerName: string;
    manufacturerItemNumber: string;
    itemDescription: string;
}

const NonStockRfqForm = ({
    fields,
    currentBillTo,
    salespersonInfo,
    shipToState,
    session,
    isAuthenticated,
    submitRfqForm,
    isMobileScreen,
}: Props) => {
    const toasterContext = useContext(ToasterContext);

    const [firstName, setFirstName] = useState(session?.firstName);
    const [lastName, setLastName] = useState(session?.lastName);
    const [phoneNumber, setPhoneNumber] = useState(session?.properties?.phoneNumber);
    const [email, setEmail] = useState(session?.email);
    const [notes, setNotes] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [lines, setLines] = useState<LineModel[]>([
        { manufacturerName: "", manufacturerItemNumber: "", itemDescription: "" },
    ]);

    const [errorMessage, setErrorMessage] = useState("");
    const formId = "nonStockRfqForm";

    const getRequiredErrorMsg = (name: string) => {
        return `${translate("{0} is required", name)}`;
    };

    if (!isAuthenticated) {
        return null;
    }

    const onSuccessHandler = () => {
        setErrorMessage("");
        toasterContext.addToast({
            title: "ddf",
            body: siteMessage("Custom_RfqFormSubmitSuccessMsg"),
            messageType: "success",
        });

        //  XNG-Changes: SC-1070
        setFirstName("");
        setLastName("");
        setPhoneNumber("");
        setEmail("");
        setNotes("");
        setLines([{ manufacturerName: "", manufacturerItemNumber: "", itemDescription: "" }]);
    };

    const submitHandler = (event: React.FormEvent<HTMLFormElement>) => {
        setErrorMessage("");
        event.preventDefault();
        event.stopPropagation();
        setIsSubmitting(true);

        const errors = xValidateOnSubmit(formId);
        if (errors.length > 0) {
            return;
        }

        submitRfqForm({
            rfqForm: {
                firstName,
                lastName,
                phoneNumber,
                email,
                notes,
                manufacturerItems: lines,
            },
            onSuccess: () => {
                setIsSubmitting(false);
                onSuccessHandler();
            },
            onError: (error: string) => {
                setIsSubmitting(false);
                setErrorMessage(error);
            },
        });
    };

    const addNewRow = (e: React.MouseEvent<Element, MouseEvent>) => {
        e.preventDefault();
        setLines([...lines, { manufacturerName: "", manufacturerItemNumber: "", itemDescription: "" }]);
    };

    const removeLine = (event: any, index: number) => {
        event.preventDefault();
        event.stopPropagation();
        if (lines.length === 1) {
            toasterContext.addToast({
                title: "df",
                body: siteMessage("Custom_nonStockRfqForm_line_cant_be_removed"),
                messageType: "danger",
            });
            return;
        }
        const newLines = [...lines.slice(0, index), ...lines.slice(index + 1)];
        setLines(newLines);
    };

    const updateLineAtIndex = (index: number, fieldName: string, newValue: string) => {
        const updatedLines = lines.map((item, i) => (i === index ? { ...item, [fieldName]: newValue } : item));
        setLines(updatedLines);
    };

    return (
        <StyledForm {...styles.form} onSubmit={submitHandler} noValidate>
            <StyledWrapper>
                <Typography {...styles.title}>{translate("Request For Quote")}</Typography>
                <Typography {...styles.heading}>{parse(fields.heading, xParserOptions)}</Typography>
                <Typography {...styles.subHeading}>{parse(fields.subheading, xParserOptions)}</Typography>
                <StyledWrapper {...styles.separator} />
                <StyledWrapper {...styles.infoWrapper}>
                    {currentBillTo?.value?.customerNumber && (
                        <SmallHeadingAndText
                            heading={translate("Bill-To Account")}
                            text={currentBillTo?.value?.customerNumber}
                            extendedStyles={styles.infoTextSmallHeading}
                        />
                    )}
                    {(shipToState.value?.customerSequence || currentBillTo?.value?.customerNumber) && (
                        <SmallHeadingAndText
                            heading={translate("Ship-To Account")}
                            text={shipToState.value?.customerSequence || currentBillTo?.value?.customerNumber || ""}
                            extendedStyles={styles.infoTextSmallHeading}
                        />
                    )}
                    {salespersonInfo && salespersonInfo.name && (
                        <SmallHeadingAndText
                            heading={translate("Sales Representative")}
                            text={salespersonInfo.name}
                            extendedStyles={styles.infoTextSmallHeading}
                        />
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.textFieldsWrapper}>
                    <StyledWrapper {...styles.sharedTextFieldStyles}>
                        <TextField
                            label={translate("First Name")}
                            value={firstName}
                            data-test-selector="nonStockRfqForm_firstName"
                            onChange={e => setFirstName(e.currentTarget.value)}
                            error={isSubmitting && !firstName && getRequiredErrorMsg(`${translate("First Name")}`)}
                            type="text"
                            maxLength={maxLengthAttr.userName}
                            required
                            {...{ formId }}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.sharedTextFieldStyles}>
                        <TextField
                            label={translate("Last Name")}
                            value={lastName}
                            data-test-selector="nonStockRfqForm_firstName"
                            onChange={e => setLastName(e.currentTarget.value)}
                            error={isSubmitting && !lastName && getRequiredErrorMsg(`${translate("Last Name")}`)}
                            type="text"
                            maxLength={maxLengthAttr.userName}
                            required
                            {...{ formId }}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.sharedTextFieldStyles}>
                        <TextField
                            type="phone"
                            label={translate("Phone Number")}
                            onChange={e => setPhoneNumber(e.currentTarget.value)}
                            value={phoneNumber}
                            data-test-selector="nonStockRfqForm_phone"
                            required
                            {...{ formId }}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.sharedTextFieldStyles}>
                        <TextField
                            type="email"
                            label={translate("Email Address")}
                            onChange={e => setEmail(e.currentTarget.value)}
                            value={email}
                            data-test-selector="nonStockRfqForm_email"
                            maxLength={maxLengthAttr.email}
                            required
                            {...{ formId }}
                            error={isSubmitting && !email && getRequiredErrorMsg(`${translate("Email Address")}`)}
                        />
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.separator} />
                <StyledWrapper {...styles.linesWrapper}>
                    <StyledWrapper {...styles.linesHeader}>
                        <StyledWrapper {...styles.mfrName}>{translate("Mfr Name (optional)")}</StyledWrapper>
                        <StyledWrapper {...styles.mfrNum}>{translate("Mfr Item # (optional)")}</StyledWrapper>
                        <StyledWrapper {...styles.itemDescription}>{translate("Item Description")}</StyledWrapper>
                    </StyledWrapper>
                    {isMobileScreen && (
                        <Typography variant="h4" as="p">
                            {translate("Items")}
                        </Typography>
                    )}
                    {lines &&
                        lines.length > 0 &&
                        lines.map((line, index) => {
                            return (
                                <StyledWrapper key={index} {...styles.lineWrapper}>
                                    <StyledWrapper {...styles.mfrNameField}>
                                        <TextField
                                            label={isMobileScreen && translate("Mfr Name (optional)")}
                                            value={line.manufacturerName}
                                            placeholder={translate("Mfr Name")}
                                            {...{ messageLabel: `${translate("Mfr Name")}` }}
                                            onChange={e =>
                                                updateLineAtIndex(index, "manufacturerName", e.currentTarget.value)
                                            }
                                            maxLength={maxLengthAttr.userName}
                                        ></TextField>
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.mfrNumField}>
                                        <TextField
                                            label={isMobileScreen && translate("Mfr Item # (optional)")}
                                            value={line.manufacturerItemNumber}
                                            placeholder={translate("00000")}
                                            {...{ messageLabel: `${translate("Mfr Number")}` }}
                                            maxLength={maxLengthAttr.userName}
                                            onChange={e =>
                                                updateLineAtIndex(
                                                    index,
                                                    "manufacturerItemNumber",
                                                    e.currentTarget.value,
                                                )
                                            }
                                        ></TextField>
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.descriptionField}>
                                        <TextField
                                            label={isMobileScreen && translate("Item Description")}
                                            value={line.itemDescription}
                                            placeholder={translate("Item Description")}
                                            required
                                            {...{ messageLabel: `${translate("Item Description")}` }}
                                            {...{ formId }}
                                            error={
                                                isSubmitting &&
                                                !line.itemDescription &&
                                                getRequiredErrorMsg(`${translate("Item Description")}`)
                                            }
                                            maxLength={maxLengthAttr.userName}
                                            onChange={e =>
                                                updateLineAtIndex(index, "itemDescription", e.currentTarget.value)
                                            }
                                        ></TextField>
                                    </StyledWrapper>
                                    <Link {...styles.remove} aria-label="remove" onClick={e => removeLine(e, index)} />
                                </StyledWrapper>
                            );
                        })}

                    <Link {...styles.addItemLink} onClick={addNewRow}>
                        {translate("Add New Item")}
                    </Link>
                </StyledWrapper>
                <StyledWrapper {...styles.notesWrapper}>
                    <TextArea
                        placeholder={translate(
                            "Please add any additional notes or requirements for your order request here",
                        )}
                        label={translate("Notes")}
                        onChange={e => setNotes(e.currentTarget.value)}
                        value={notes}
                        maxLength={maxLengthAttr.note}
                    />
                </StyledWrapper>
                {errorMessage && (
                    <Notify {...styles.errorMsg} type="danger">
                        {errorMessage}
                    </Notify>
                )}

                <StyledWrapper {...styles.submitWrapper}>
                    <Button
                        aria-label={translate("Send")}
                        type="submit"
                        data-test-selector="nonStockRfqForm_submitButton"
                    >
                        {translate("Send")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        </StyledForm>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(NonStockRfqForm),
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        fieldDefinitions: [
            {
                fieldType: "Contextual",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Heading",
                defaultValue: "",
                extendedConfig: { height: 100 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
            },
            {
                fieldType: "Contextual",
                name: fields.subheading,
                editorTemplate: "RichTextField",
                displayName: "Subheading",
                defaultValue: "",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
            },
        ],
    },
};

export default widgetModule;
